import { Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import Breadcrumb from '../../components/layout/Breadcrumb';
import { getFaqForRoute } from '../../data/faq';
import { relatedSites } from '../../data/related';
import type { RelatedBlock, RelatedSite } from '../../data/related/types';

// ===== 関連サイト 詳細ページ 共通描画(2026-09-18 作り直し) =====
// 章立て・分量は THE BUTLER の RelatedSiteDetailPage を正典に(hero → 章帯付きブロック ×10 前後 → FAQ → 他の関連サイト)、
// 見た目は当協会サイトの流儀(NavyBand・紺#1a2942・真鍮金#a38e65・BTN-08・細罫)のみを使う。
// FAQ は src/data/faq.json(getFaqForRoute)を唯一の正本として描画し、prerender の FAQPage JSON-LD と一致させる。
// 外部公式 URL は新規タブ + rel="noopener noreferrer"。ヒーロー直下・中間・末尾の 3 箇所に公式 CTA を置く。

const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";
// SP(375px)では長い公式ラベルが折り返せるよう nowrap は sm 以上のみ(実測: 「Queen's Butler 公式サイトを見る」が right=404px で横はみ出し)
const BTN_OFFICIAL = "relative inline-flex items-center justify-center gap-2 bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-6 sm:px-[46px] py-[18px] text-sm tracking-[0.12em] sm:tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer max-w-full text-center sm:whitespace-nowrap";
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV'];

export function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

/** `{強調}` を金色 span に変換した見出し */
function Accent({ text, gold = 'text-[#a38e65]' }: { text: string; gold?: string }) {
  const parts = text.split(/(\{[^}]+\})/g).filter(Boolean);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith('{') ? <span key={i} className={gold}>{p.slice(1, -1)}</span> : <span key={i}>{p}</span>,
      )}
    </>
  );
}

const P = "font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]";
const WRAP = 'max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10';

function Block({ b, no, isEn, url }: { b: RelatedBlock; no: string; isEn: boolean; url: string }) {
  if (b.kind === 'twocol') {
    return (
      <>
        <NavyBand no={no} ja={b.band} en={b.bandEn} />
        <section className="py-12 sm:py-16 bg-white">
          <div className={WRAP}>
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div className={`text-left ${b.reverse ? 'order-2' : 'order-2 lg:order-1'}`}>
                <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
                  <Accent text={b.h} />
                </h3>
                {b.body.map((p, i) => (
                  <p key={i} className={`${P} mb-4 last:mb-0`}>{p}</p>
                ))}
                {b.subs && (
                  <div className="mt-6 border-t border-[#e2ddd2]">
                    {b.subs.map((s, i) => (
                      <div key={i} className="border-b border-[#e2ddd2] py-4">
                        <h4 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{s.t}</h4>
                        <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.9]">{s.b}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className={b.reverse ? 'order-1' : 'order-1 lg:order-2'}>
                <img
                  src={b.image.src}
                  alt={b.image.alt}
                  loading="lazy"
                  className="w-full h-[300px] sm:h-[400px] md:h-[460px] object-cover border border-[#a38e65]/30"
                  style={{ objectPosition: b.image.pos ?? 'top' }}
                />
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  if (b.kind === 'panel') {
    return (
      <>
        <NavyBand no={no} ja={b.band} en={b.bandEn} />
        <section className="py-12 sm:py-16 bg-white">
          <div className={WRAP}>
            <div className="grid lg:grid-cols-2 items-stretch border border-[#e2ddd2] overflow-hidden">
              <div className="relative min-h-[300px] lg:min-h-[440px] bg-black order-1">
                <img src={b.image.src} alt={b.image.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: b.image.pos ?? 'top' }} />
              </div>
              <div className="order-2 bg-[#1a2942] flex flex-col justify-center px-8 sm:px-12 lg:px-14 py-12 lg:py-16 text-left">
                <span className="block font-['Playfair_Display',serif] italic text-[#a38e65] text-sm tracking-[0.06em] mb-4">{b.eyebrow}</span>
                <h3 className="jba-display font-medium text-[#f8f6f0] text-2xl sm:text-3xl leading-[1.5] tracking-[0.02em] mb-6">
                  <Accent text={b.h} gold="text-[#d7b779]" />
                </h3>
                {b.body.map((p, i) => (
                  <p key={i} className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] mb-4 last:mb-0">{p}</p>
                ))}
                {b.subs && (
                  <div className="mt-6 border-t border-[#a38e65]/30">
                    {b.subs.map((s, i) => (
                      <div key={i} className="border-b border-[#a38e65]/30 py-4">
                        <h4 className="jba-display font-medium text-[#d7b779] text-lg mb-2">{s.t}</h4>
                        <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#e7e2d8] leading-[1.9]">{s.b}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  if (b.kind === 'cards') {
    const cols = b.cols ?? (b.cards.length >= 4 ? 4 : b.cards.length === 2 ? 2 : 3);
    const grid = cols === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : cols === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3';
    return (
      <>
        <NavyBand no={no} ja={b.band} en={b.bandEn} />
        <section className="py-12 sm:py-16 bg-white">
          <div className={WRAP}>
            <p className={`${P} max-w-4xl mb-10 text-left`}>{b.lead}</p>
            <div className={`grid grid-cols-1 ${grid} gap-8 lg:gap-10`}>
              {b.cards.map((c, i) => (
                <article key={c.title} className="group flex flex-col bg-white border border-[#e2ddd2] hover:border-[#a38e65]/70 transition-colors duration-300">
                  <div className="relative h-52 sm:h-56 overflow-hidden">
                    <img src={c.image.src} alt={c.image.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: c.image.pos ?? 'top' }} />
                    <span className="absolute top-3 left-3 font-['Playfair_Display',serif] text-[#f8f6f0] text-2xl drop-shadow-lg">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="flex flex-col flex-1 p-7 text-left">
                    <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1.5">{c.en}</span>
                    <h4 className="jba-display font-medium text-[#1a2942] text-xl mb-3 leading-[1.4]">{c.title}</h4>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{c.desc}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </>
    );
  }

  if (b.kind === 'tiers') {
    return (
      <>
        <NavyBand no={no} ja={b.band} en={b.bandEn} />
        <section className="py-12 sm:py-16 bg-white">
          <div className={WRAP}>
            <p className={`${P} max-w-4xl mb-10 text-left`}>{b.lead}</p>
            <div className="border-t border-[#e2ddd2]">
              {b.tiers.map((t) => (
                <div key={t.title} className="grid grid-cols-[auto_1fr] md:grid-cols-[72px_200px_1fr] gap-x-6 gap-y-3 items-center border-b border-[#e2ddd2] py-6 text-left">
                  <span className="font-['Playfair_Display',serif] text-[#a38e65] text-3xl leading-none">{t.lv}</span>
                  <div className="col-span-1 md:col-span-1 h-40 md:h-32 w-full max-w-[200px]">
                    <img src={t.image.src} alt={t.image.alt} loading="lazy" className="w-full h-full object-cover border border-[#a38e65]/30" style={{ objectPosition: t.image.pos ?? 'top' }} />
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{t.tag}</span>
                    <h4 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2 leading-[1.4]">{t.title}</h4>
                    <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.9]">{t.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </>
    );
  }

  // official
  return (
    <>
      <NavyBand no={no} ja={b.band} en={b.bandEn} />
      <section className="py-12 sm:py-16 bg-white">
        <div className={WRAP}>
          <div className="bg-[#f8f6f0] border border-[#a38e65]/30 p-8 sm:p-10 md:p-14 text-center max-w-4xl mx-auto">
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl leading-[1.5] mb-4">
              {b.h}
              <span className="block text-lg text-[#8f7a56] mt-2 font-normal">{b.sub}</span>
            </h3>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#4a4638] leading-[1.95] max-w-2xl mx-auto mb-8">{b.body}</p>
            <OfficialLink label={b.label} isEn={isEn} url={url} />
          </div>
        </div>
      </section>
    </>
  );
}

function OfficialLink({ label, isEn, url }: { label: string; isEn: boolean; url: string }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={BTN_OFFICIAL} aria-label={isEn ? `${label} (opens in a new tab)` : `${label}（新しいタブで開きます）`}>
      {label} <i className="ri-external-link-line"></i>
    </a>
  );
}

export default function RelatedDetail({ slug, lang }: { slug: RelatedSite['slug']; lang: 'ja' | 'en' }) {
  const isEn = lang === 'en';
  const site = relatedSites.find((s) => s.slug === slug)!;
  const c = isEn ? site.en : site.ja;
  const base = isEn ? '/en/related' : '/related';
  const route = `${base}/${slug}`;
  const faqs = getFaqForRoute(route);
  const others = relatedSites.filter((s) => s.slug !== slug);
  const breadcrumbs = [
    { label: isEn ? 'Home' : 'ホーム', href: isEn ? '/en/' : '/' },
    { label: isEn ? 'Related Sites' : '関連サイト', href: isEn ? '/en/related/' : '/related' },
    { label: c.name },
  ];
  const midIdx = Math.floor(c.blocks.length / 2);
  const t = (ja: string, en: string) => (isEn ? en : ja);

  return (
    <Layout>
      {/* ===== ヒーロー — 全幅ビルボード型 ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[480px] lg:min-h-[560px] flex items-center">
        <img src={c.hero.src} alt={c.hero.alt} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: c.hero.pos ?? 'top' }} />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.7)_50%,rgba(16,20,26,0.4)_100%)]"></div>
        <div className={`relative z-10 w-full ${WRAP} pt-32 pb-14 lg:pt-36 lg:pb-20`}>
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">{c.heroKicker}</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.2vw,42px)] leading-[1.42] tracking-[0.04em] mb-6">
              {c.heroTitle.map((l) => <span key={l} className="block">{l}</span>)}
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">{c.heroLead}</p>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* 公式CTA ①: ヒーロー直下 */}
      <section className="pb-10 bg-white">
        <div className={`${WRAP} text-center`}>
          <OfficialLink label={c.officialLabel} isEn={isEn} url={site.officialUrl} />
        </div>
      </section>

      {c.blocks.map((b, i) => (
        <div key={`${slug}-${i}`}>
          <Block b={b} no={ROMAN[i]} isEn={isEn} url={site.officialUrl} />
          {i === midIdx && (
            /* 公式CTA ②: 中間 */
            <section className="pb-4 bg-white">
              <div className={`${WRAP} text-center`}>
                <OfficialLink label={c.officialLabel} isEn={isEn} url={site.officialUrl} />
              </div>
            </section>
          )}
        </div>
      ))}

      {/* ===== よくあるご質問（faq.json を正本に自前描画。Layout の pagesWithOwnFaq 登録済み）===== */}
      <NavyBand no={ROMAN[c.blocks.length]} ja={t('よくあるご質問', 'Frequently asked questions')} en="FAQ" />
      <section className="py-12 sm:py-16 bg-white">
        <div className={WRAP}>
          <div className="max-w-4xl mx-auto border-t border-[#e2ddd2]">
            {faqs.map((f) => (
              <div key={f.q} className="border-b border-[#e2ddd2] py-6 text-left">
                <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mb-3 flex items-start gap-3">
                  <span className="font-['Playfair_Display',serif] text-[#a38e65] text-xl leading-none pt-0.5">Q.</span><span>{f.q}</span>
                </h3>
                <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.95] pl-8 whitespace-pre-line">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ほかの関連サイト＋CTA ===== */}
      <section className="py-12 sm:py-16 bg-[#f8f6f0]">
        <div className={WRAP}>
          <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-2xl mb-8 text-center">{t('ほかの関連サイト', 'Other related sites')}</h3>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto mb-12">
            {others.map((o) => {
              const oc = isEn ? o.en : o.ja;
              return (
                <Link key={o.slug} to={`${base}/${o.slug}${isEn ? '/' : ''}`} className="group flex flex-col bg-white border border-[#e2ddd2] hover:border-[#a38e65]/70 transition-colors duration-300 text-left">
                  <div className="relative h-40 overflow-hidden">
                    <img src={oc.hub.src} alt={oc.hub.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: oc.hub.pos ?? 'top' }} />
                  </div>
                  <div className="flex items-center justify-between p-6">
                    <div>
                      <span className="block font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">{oc.role}</span>
                      <span className="jba-display font-medium text-[#1a2942] text-lg group-hover:text-[#8f7a56] transition-colors">{oc.name}</span>
                    </div>
                    <span className="text-[#a38e65] text-lg group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 text-center max-w-4xl mx-auto">
            <h3 className="jba-display font-medium text-xl sm:text-2xl text-[#f8f6f0] mb-3">{t('執事の学びや資格に関心をお持ちの方へ', 'Interested in butler training or certification?')}</h3>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.9] mb-7 max-w-2xl mx-auto">{t('関連事業へのご相談は各公式サイトが窓口です。協会の資格制度や法人研修については当協会へお問い合わせください。', 'Each official site is the point of contact for its own service. For the Association\'s certification system and corporate training, please contact us directly.')}</p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
              <Link to={isEn ? '/en/qualifications/' : '/qualifications'} className={BTN08_ON_NAVY}>{t('執事資格制度を見る', 'Certification System')}</Link>
              <Link to={isEn ? '/en/contact/' : '/contact'} className={BTN08_ON_NAVY}>{t('お問い合わせ', 'Contact Us')}</Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
